/**
 * Skill Workspace — the read projection of a skill package's maintenance
 * surface (V3 screen 14). It mirrors the Decision Room grammar (a packet hook +
 * `PageHeader` + `InspectorSection` blocks) and routes its body through the
 * shared eight-state `RenderState` contract (S13) so loading / empty / normal /
 * stale / overflow / redacted / forbidden / error all read the same way.
 *
 * Lens/projection doctrine: this screen is READ-ONLY truth. It renders the
 * `GET /api/skills/:skillId/workspace` ViewPacket — readiness flags, package
 * versions, sanitized usage rollups, deployments, and improvement proposals.
 * It never mutates business state here; the operating actions (propose an
 * improvement, set review status, publish) are server endpoints that enforce
 * AccessGuard + DomainService boundaries. Agents propose; they do not publish.
 *
 * The usage-rollup section honours the worker's privacy contract: a rollup whose
 * sample fell below threshold is surfaced as a `redacted` (suppressed) row with
 * its redaction summary, never its withheld failure signatures.
 */
import * as React from "react";
import { useParams } from "react-router-dom";

import type { SkillWorkspacePacket } from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Icon } from "@/components/ui/icon";
import { ErrorState } from "@/components/ui/states";
import { RedactedBadge } from "@/components/app/RenderState";
import { RenderState, type RenderStateKind } from "@/components/app/RenderState";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Field,
  FieldGrid,
  InspectorSection,
  RefLink,
} from "@/components/inspector";
import { api } from "@/lib/api";
import { formatDateTime, formatNumber, humanize } from "@/lib/format";

/** Row aliases derived from the packet so we never re-declare the wire shape. */
type WorkspaceVersion = SkillWorkspacePacket["versions"][number];
type WorkspaceRollup = SkillWorkspacePacket["usage_rollups"][number];
type WorkspaceDeployment = SkillWorkspacePacket["deployments"][number];
type WorkspaceProposal = SkillWorkspacePacket["proposal_artifacts"][number];

function useSkillWorkspacePacket(skillId: string) {
  const [data, setData] = React.useState<SkillWorkspacePacket | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((value) => value + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<SkillWorkspacePacket>(`/skills/${encodeURIComponent(skillId)}/workspace`)
      .then((packet) => {
        if (!cancelled) setData(packet);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load Skill Workspace.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [skillId, reloadKey]);

  return { data, loading, error, reload };
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <p className="font-mono text-sm tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

/** A readiness gate rendered as a yes/no badge with its semantic icon. */
function ReadyFlag({ label, ready }: { label: string; ready: boolean }) {
  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <Badge variant={ready ? "default" : "outline"}>
          <Icon name={ready ? "resultOk" : "reject"} aria-hidden />
          {ready ? "Yes" : "No"}
        </Badge>
      </CardContent>
    </Card>
  );
}

/**
 * A usage rollup row. A rollup whose sample fell below the privacy threshold is
 * `privacy_visibility === "suppressed"`; we never render its failure signatures,
 * only the non-leaky redaction summary.
 */
function RollupCard({ rollup }: { rollup: WorkspaceRollup }) {
  const suppressed = rollup.privacy_visibility === "suppressed";
  const passRate =
    rollup.invocations > 0
      ? `${Math.round((rollup.successes / rollup.invocations) * 100)}%`
      : "—";
  return (
    <Card data-testid="rollup-card">
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base">Window to {formatDateTime(rollup.computed_at)}</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {formatNumber(rollup.invocations)} invocations · pass rate {passRate}
            </p>
          </div>
          {suppressed ? (
            <RedactedBadge label="usage sample" />
          ) : (
            <Badge variant="secondary">{humanize(rollup.privacy_visibility)}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <FieldGrid>
          <Field label="Successes">{formatNumber(rollup.successes)}</Field>
          <Field label="Failures">{formatNumber(rollup.failures)}</Field>
          <Field label="Validation failures">{formatNumber(rollup.validation_failures)}</Field>
          <Field label="Sample size">{formatNumber(rollup.sample_size)}</Field>
        </FieldGrid>
        {suppressed ? (
          <p className="mt-3 text-xs text-muted-foreground [overflow-wrap:anywhere]">
            {rollup.redaction_summary}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function VersionsTable({ versions }: { versions: readonly WorkspaceVersion[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Version</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Risk</TableHead>
          <TableHead>Content hash</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {versions.map((version) => (
          <TableRow key={version.id} data-testid="version-row">
            <TableCell className="font-mono tabular-nums">v{version.version}</TableCell>
            <TableCell>
              <StatusBadge status={version.status} />
            </TableCell>
            <TableCell>
              <Badge variant="outline">{humanize(version.risk_level)}</Badge>
            </TableCell>
            <TableCell className="font-mono text-xs text-muted-foreground [overflow-wrap:anywhere]">
              {version.content_hash}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function DeploymentCard({ deployment }: { deployment: WorkspaceDeployment }) {
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle className="text-base font-mono text-sm [overflow-wrap:anywhere]">
            {deployment.context_id}
          </CardTitle>
          {deployment.deployment_status ? (
            <StatusBadge status={deployment.deployment_status} />
          ) : (
            <Badge variant="outline">Unbound</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <FieldGrid>
          <Field label="Context">
            <RefLink resource="contexts" id={deployment.context_id} />
          </Field>
          <Field label="Pinned version">
            {deployment.pinned_package_version_id ? (
              <span className="font-mono text-xs">{deployment.pinned_package_version_id}</span>
            ) : (
              "—"
            )}
          </Field>
          <Field label="Upgrade mode">{deployment.upgrade_mode ?? "—"}</Field>
          <Field label="Lock hash">
            <span className="font-mono text-xs [overflow-wrap:anywhere]">
              {deployment.resolved_package_lock_hash ?? "—"}
            </span>
          </Field>
        </FieldGrid>
      </CardContent>
    </Card>
  );
}

function ProposalCard({ proposal }: { proposal: WorkspaceProposal }) {
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle className="text-base [overflow-wrap:anywhere]">{proposal.title}</CardTitle>
          <StatusBadge status={proposal.status} />
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">Improvement proposal</Badge>
          <RefLink resource="artifacts" id={proposal.id} label="Open artifact" />
        </div>
      </CardContent>
    </Card>
  );
}

function SkillWorkspaceContent({
  packet,
  reload,
}: {
  packet: SkillWorkspacePacket;
  reload: () => void;
}) {
  const readiness = packet.readiness;
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <ReadyFlag label="Executable" ready={readiness.executable} />
        <ReadyFlag label="Has Contracts" ready={readiness.has_contracts} />
        <ReadyFlag label="Has Deployment" ready={readiness.has_deployment} />
        <ReadyFlag label="Recent Rollup" ready={readiness.has_recent_rollup} />
      </div>

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Skill">
            <RefLink resource="skills" id={packet.skill.id} label={packet.skill.name} />
          </Field>
          <Field label="Status">
            <StatusBadge status={packet.skill.status} />
          </Field>
          <Field label="Maturity">{humanize(packet.skill.maturity_stage)}</Field>
          <Field label="Live version">
            <span className="font-mono tabular-nums">v{packet.skill.version}</span>
          </Field>
          <Field label="Workspace context">
            <RefLink resource="contexts" id={packet.workspace_context.id} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric label="Versions" value={packet.versions.length} />
        <Metric label="Usage rollups" value={packet.usage_rollups.length} />
        <Metric label="Deployments" value={packet.deployments.length} />
        <Metric label="Open proposals" value={packet.proposal_artifacts.length} />
      </div>

      <InspectorSection
        title="Versions"
        aside={
          <Button variant="outline" size="sm" onClick={reload}>
            <Icon name="reopen" data-icon="inline-start" aria-hidden />
            Refresh
          </Button>
        }
      >
        {packet.versions.length === 0 ? (
          <RenderState
            state="empty"
            empty={{
              icon: "packages",
              title: "No package versions yet.",
              hint: "Versions appear once an improvement is proposed against this skill.",
            }}
          />
        ) : (
          <VersionsTable versions={packet.versions} />
        )}
      </InspectorSection>

      <InspectorSection title="Usage Rollups">
        {packet.usage_rollups.length === 0 ? (
          <RenderState
            state="empty"
            empty={{
              icon: "signals",
              title: "No usage rollups yet.",
              hint: "Sanitized usage rollups appear after the skill runs in a deployment.",
            }}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {packet.usage_rollups.map((rollup) => (
              <RollupCard key={rollup.id} rollup={rollup} />
            ))}
          </div>
        )}
      </InspectorSection>

      <InspectorSection title="Deployments">
        {packet.deployments.length === 0 ? (
          <RenderState
            state="empty"
            empty={{
              icon: "contexts",
              title: "No deployments yet.",
              hint: "This skill is not yet deployed into any context.",
            }}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {packet.deployments.map((deployment) => (
              <DeploymentCard key={deployment.context_id} deployment={deployment} />
            ))}
          </div>
        )}
      </InspectorSection>

      <InspectorSection title="Improvement Proposals">
        {packet.proposal_artifacts.length === 0 ? (
          <RenderState
            state="empty"
            empty={{
              icon: "transitionProposals",
              title: "No improvement proposals.",
              hint: "Agents propose improvements from usage; humans review and publish.",
            }}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {packet.proposal_artifacts.map((proposal) => (
              <ProposalCard key={proposal.id} proposal={proposal} />
            ))}
          </div>
        )}
      </InspectorSection>
    </div>
  );
}

export function SkillWorkspaceRoute() {
  const { id } = useParams<{ id: string }>();

  if (!id) return <ErrorState message="Missing skill id." />;

  return <SkillWorkspacePage skillId={id} />;
}

function SkillWorkspacePage({ skillId }: { skillId: string }) {
  const { data, loading, error, reload } = useSkillWorkspacePacket(skillId);

  const state: RenderStateKind = loading
    ? "loading"
    : error
      ? "error"
      : "normal";

  return (
    <div className="max-w-6xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Skill Workspace"
          subtitle="Maintenance surface for a skill package: readiness, versions, sanitized usage rollups, deployments, and improvement proposals."
          icon="skills"
        />
        <RenderState
          state={state}
          loadingRows={6}
          error={error}
          onRetry={reload}
        >
          {data ? <SkillWorkspaceContent packet={data} reload={reload} /> : null}
        </RenderState>
      </div>
    </div>
  );
}

export default SkillWorkspaceRoute;
